import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import { fetchBlobObjectUrl } from "../../api/blobFetch";
import {
  getAllFilesApiV1FilesGet,
  getFileApiV1FilesIdGet,
  getFileUploadSasUrlApiV1FilesUploadSasUrlPost,
} from "../../api/generated/sdk.gen";
import {
  DocSorterFileSchema,
  type FileUploadSasUrlRequestSchema,
  type FileUploadSasUrlResponseSchema,
  GetAllFilesApiV1FilesGetData,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { FileWithIndexing } from "../../types/file-indexing";
import { buildApiErrorMessage } from "../../utils/notification";

const FAST_POLL_MS = 10 * 1000; // 10s — files mid-index
const SLOW_POLL_MS = 5 * 60 * 1000; // 5 min — steady state

const hasFilesIndexing = (files: DocSorterFileSchema[] | undefined): boolean => {
  if (!files) return false;
  return files.some((f) => {
    const aug = f as FileWithIndexing;
    if (aug.indexing_status) {
      return (
        aug.indexing_status === "PENDING" || aug.indexing_status === "IN_PROGRESS"
      );
    }
    // Fallback for old backend without indexing_status: rely on is_indexed +
    // file status COMPLETED to infer the file should be indexed but isn't yet.
    return f.status === "COMPLETED" && aug.is_indexed === false;
  });
};

const hasFilesProcessing = (files: DocSorterFileSchema[] | undefined): boolean => {
  if (!files) return false;
  return files.some(
    (f) => f?.status === "PROCESSING" || f?.status === "TO_BE_PROCESSED"
  );
};

/**
 * Fetch a case document's bytes and return an object URL for the viewer.
 *
 * MDRS-503 replaced `GET /files/{id}/sas-url` with `GET /files/{id}/content`.
 * The old endpoint minted a signed Azure URL, so the fetch went straight to
 * blob storage and our ownership check never ran on it — these are the
 * examinee's clinical documents, so a forwarded link was a PHI leak. The proxy
 * re-authorises every fetch, which also means the bytes have to come through
 * `apiClient` (bearer token) rather than being handed to an `<iframe src>`.
 *
 * Pass `mimeType` when the viewer keys off the blob's type (the PDF viewer and
 * `docx-preview` both do).
 *
 * The caller owns the returned object URL and should revoke it on unmount.
 *
 * @param fileId - The doc sorter file id
 * @returns An object URL (`blob:...`) for the document's bytes
 * @throws Error if `fileId` is falsy, or if the request fails
 */
export async function fetchFileContentUrl(
  fileId: string | undefined,
  mimeType?: string
): Promise<string> {
  if (!fileId) {
    // Fail loud on the client instead of shipping a literal `{file_id}` path
    // param, which the backend rejects as a raw 500 (MDRS-137).
    throw new Error("Failed to fetch file content: missing file id");
  }

  return fetchBlobObjectUrl(
    `/api/v1/files/${encodeURIComponent(fileId)}/content`,
    mimeType
  );
}

/**
 * Fetch all files with optional filtering
 * @param case_number - Filter by case number (required for files endpoint)
 * @param status - Filter by status (comma-separated list)
 * @param classification - Filter by classification
 * @param created_at - Filter by created at date
 * @param created_at_end - End of the created_at day (the next local midnight)
 * @param issue_date - Filter by issue date
 * @param sort_by - Sort order: recently_added, recently_updated, or oldest
 * @param enabled - Whether the query should run
 * @returns Query result with files data
 */
export const useFiles = ({
  case_number,
  status,
  classification,
  created_at,
  created_at_end,
  issue_date,
  sort_by,
  enabled = true,
}: {
  enabled?: boolean;
} & GetAllFilesApiV1FilesGetData["query"] = {}) => {
  return useQuery({
    queryKey: [
      CLIENT_KEYS.FILES,
      {
        case_number,
        status,
        classification,
        created_at,
        created_at_end,
        issue_date,
        sort_by,
      },
    ],
    queryFn: async () => {
      const response = await getAllFilesApiV1FilesGet({
        client: apiClient,
        query: {
          case_number,
          status,
          classification,
          created_at,
          created_at_end,
          issue_date,
          sort_by,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to fetch files", response.error)
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_number, // Files endpoint requires case_number
    refetchInterval: (query) => {
      // Poll fast while either the AI summary generator is still working OR
      // any file is mid-index, so the UI flips to COMPLETED / INDEXED quickly.
      // Otherwise fall back to a relaxed background refresh.
      const data = query.state.data as DocSorterFileSchema[] | undefined;
      return hasFilesProcessing(data) || hasFilesIndexing(data)
        ? FAST_POLL_MS
        : SLOW_POLL_MS;
    },
  });
};

/**
 * Fetch a single file by ID
 * @param fileId - The ID of the file to fetch
 * @param enabled - Whether the query should run
 * @returns Query result with file data
 */
export const useFile = ({
  fileId,
  enabled = true,
}: {
  fileId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.FILES, fileId],
    queryFn: async () => {
      const response = await getFileApiV1FilesIdGet({
        path: { id: fileId },
        client: apiClient,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to fetch file", response.error)
        );
      }

      return response.data;
    },
    enabled: enabled && !!fileId,
  });
};

/**
 * Fetch a write-only SAS URL for a single blob targeted at the upload path.
 * Backend validates the case belongs to the requesting user before issuing.
 */
export async function fetchFileUploadSasUrl(
  payload: FileUploadSasUrlRequestSchema
): Promise<FileUploadSasUrlResponseSchema> {
  const response = await getFileUploadSasUrlApiV1FilesUploadSasUrlPost({
    client: apiClient,
    body: payload,
  });

  // MDRS-624: deliberately NOT "Failed to fetch ...". getFriendlyErrorMessage
  // matches the substring "failed to fetch" and answers it with "Unable to load
  // the requested information" — a branch that sits above the permission branch,
  // so the prefix alone was swallowing this endpoint's 403 and telling the user
  // to retry something that can only fail again. The prefix must not impersonate
  // a transport failure; a real one carries its own wording from the browser.
  if ("error" in response && response.error) {
    throw new Error(
      buildApiErrorMessage("Couldn't get an upload URL", response.error)
    );
  }

  if (!response.data) {
    throw new Error("Couldn't get an upload URL: empty response");
  }

  return response.data;
}

/**
 * Fetch all files for a specific case
 * This is a simpler version of useFiles specifically for getting all files for a case
 * without needing to provide other filters
 *
 * @param case_number - The case number to fetch files for
 * @param enabled - Whether the query should run
 * @returns Query result with exported files data
 */
export const useAllCaseFiles = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.FILES, case_number],
    queryFn: async () => {
      const response = await getAllFilesApiV1FilesGet({
        client: apiClient,
        query: {
          case_number,
        },
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to fetch exported files", response.error)
        );
      }

      return response.data;
    },
    enabled: enabled && !!case_number,
  });
};
